import { useMemo } from 'react';
import { Zap, Check, Package, MessageSquare, AlertTriangle, Clock, Eye, X } from 'lucide-react';
import { toast } from '@/hooks/use-toast';

import { useTriagemAtivacao, type ItemTriagemAtivacao } from './useTriagemAtivacao';
import { useFinalizarPedidoAtivo } from './useFinalizarPedidoAtivo';
import { NumeroLinhaHaDialog } from './NumeroLinhaHaDialog';
import { TriagemAtivacaoRow } from './TriagemAtivacaoRow';
import { TriagemStandardHeaders, TriagemStandardColGroup } from './TriagemStandardColumns';
import { ehDoSegmento, rotuloSegmento, useSegmentoTriagem } from '@/components/kanban/triagem/segmentoTriagem';
import { TriagemShell } from '@/components/kanban/triagem/TriagemShell';
import { useLinhasOcupadas } from '@/components/kanban/triagem/acaoTriagem';
import { TRIAGEM_TH, TRIAGEM_LARGURA_MINIMA_PADRAO } from '@/components/kanban/triagem/triagemTabela';

interface TriagemAtivacaoModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function TriagemAtivacaoModal({ isOpen, onClose }: TriagemAtivacaoModalProps) {
  const {
    itens: itensTodos, isLoading, refetch,
    moverParaAguardandoNF, moverParaSmsEnviado, moverParaCpfCnpjDivergente, moverParaFinal,
  } = useTriagemAtivacao('INSPEÇÃO CONCLUIDA');
  /* Só a cotação clicada fica ocupada; o resto da fila continua livre
     (`triagem/acaoTriagem.ts`). O Finalizar é da linha. */
  const { ocupada, ocupar } = useLinhasOcupadas();
  const chave = (i: ItemTriagemAtivacao, acao: string) =>
    `${acao === 'finalizar' ? i.item_id : i.numero_cotacao_operadora || i.item_id}|${acao}`;
  const rodar = (i: ItemTriagemAtivacao, acao: string, fn: () => Promise<unknown>) => {
    void ocupar(chave(i, acao), fn).catch(() => undefined);
  };

  /* ⛔ CNPJ e CPF NÃO SE MISTURAM NA FILA (Douglas 17/09/2026). A régua de
     entrada desta triagem é a MESMA de sempre — o que muda é a fila mostrar um
     segmento por vez, e os contadores do topo falarem do que está na tela.
     A separação é filtro, nunca coluna nova: nada troca de fila fora da
     triagem. Lei em `triagem/segmentoTriagem.ts`. */
  const { segmento, setSegmento } = useSegmentoTriagem();
  const itens = useMemo(
    () => itensTodos.filter((r) => ehDoSegmento(r.tipo_pessoa, segmento)),
    [itensTodos, segmento],
  );

  const { finalizarPedidoAsync, numeroHaDialog } = useFinalizarPedidoAtivo();
  const uniquePedidos = new Set(itens.map(i => i.pedido_id)).size;

  const handleFinalizar = (item: typeof itens[0]) => {
    rodar(item, 'finalizar', () => finalizarPedidoAsync({
      item_id: item.item_id,
      pedido_id: item.pedido_id,
      cnpj: item.cnpj,
      razao_social: item.razao_social,
      telefone: item.telefone,
      email: item.email,
      cliente_id: item.cliente_id,
      status_anterior_id: item.status_id,
      coluna_anterior: item.coluna_atual,
      quadro_anterior: item.quadro_atual,
      acao: 'finalizar_ativo',
    }));
  };

  return (
    <>
    <TriagemShell
      isOpen={isOpen}
      onFechar={onClose}
      Icone={Zap}
      titulo="Triagem Ativação"
      contadores={{ pedidos: uniquePedidos, itens: itens.length, valor: 0 }}
      segmento={segmento}
      onSegmento={setSegmento}
      sla={null}
      fifo={false}
      legenda={[
        { key: 'manter', Icone: Clock, label: 'Manter' },
        { key: 'finalizar', Icone: Check, label: 'Finalizar' },
        { key: 'nf', Icone: Package, label: 'Aguardar NF' },
        { key: 'sms', Icone: MessageSquare, label: 'SMS Enviado' },
        { key: 'divergente', Icone: AlertTriangle, label: 'CPF/CNPJ Divergente' },
        { key: 'analisar', Icone: Eye, label: 'Analisar' },
        { key: 'cancelar', Icone: X, label: 'Cancelar' },
      ]}
      isLoading={isLoading}
      onAtualizar={async () => {
        await refetch();
        toast({ title: 'Dados atualizados', duration: 1500 });
      }}
      vazio={
        itens.length === 0
          ? { titulo: 'Nenhum item pendente', descricao: 'Todos os itens foram processados' }
          : undefined
      }
    >
      <table className="w-full" style={{ tableLayout: 'fixed', minWidth: TRIAGEM_LARGURA_MINIMA_PADRAO }}>
        <TriagemStandardColGroup extraCols={<col style={{ width: '326px' }} />} />
        <thead className="sticky top-0 z-10 bg-surface">
          <tr>
            <TriagemStandardHeaders rotuloDocumento={rotuloSegmento(segmento)} />
            <th className={TRIAGEM_TH}>Ações</th>
          </tr>
        </thead>
        <tbody>
          {itens.map((item) => (
            <TriagemAtivacaoRow
              key={item.item_id}
              item={item}
              onFinalizar={() => handleFinalizar(item)}
              onAguardarNF={() => rodar(item, 'nf', () => moverParaAguardandoNF(item.item_id))}
              onSmsEnviado={() => rodar(item, 'sms', () => moverParaSmsEnviado(item.item_id))}
              onCpfCnpjDivergente={() => rodar(item, 'divergente', () => moverParaCpfCnpjDivergente(item.item_id))}
              onMoverParaFinal={() => rodar(item, 'manter', () => moverParaFinal(item.item_id))}
              onAfterCancel={() => { void refetch(); }}
              isFinalizando={ocupada(chave(item, 'finalizar'))}
              isMovingToAguardandoNF={ocupada(chave(item, 'nf'))}
              isMovingToSmsEnviado={ocupada(chave(item, 'sms'))}
              isMovingToCpfCnpjDivergente={ocupada(chave(item, 'divergente'))}
              isMovingToFinal={ocupada(chave(item, 'manter'))}
              showAguardarNF={true}
              manterJustificado
            />
          ))}
        </tbody>
      </table>
    </TriagemShell>
    <NumeroLinhaHaDialog {...numeroHaDialog} />
    </>
  );
}
